import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store";
import { setSelectedPair } from "store/slices/tradingSlice";
import { useState, useMemo } from "react";
import { TradingAssets, useDebounce } from "shared/model";
import { sortPairs } from "./sortPairs";
import { setStoredPair } from "./pairSessionStorage";

export const usePairSelect = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { currencyPairs, selectedPair } = useSelector(
    (state: RootState) => state.trading,
  );
  const userId = useSelector((state: RootState) => state.auth.user?.id);

  const [manualTab, setManualTab] = useState<TradingAssets | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const activeTab = useMemo(() => {
    if (search) return null;

    if (manualTab) return manualTab;

    const found = currencyPairs.find((pair) => pair.symbol === selectedPair);

    return found?.assetType ?? null;
  }, [search, manualTab, selectedPair, currencyPairs]);

  const filteredPairs = useMemo(() => {
    const matched = debouncedSearch
      ? currencyPairs.filter((pair) =>
          pair.symbol.toLowerCase().includes(debouncedSearch.toLowerCase()),
        )
      : currencyPairs.filter((pair) => pair.assetType === activeTab);

    return sortPairs(matched);
  }, [debouncedSearch, currencyPairs, activeTab]);

  const handleSelect = (pair: string) => {
    dispatch(setSelectedPair(pair));
    if (userId != null) {
      setStoredPair(userId, pair);
    }
    setSearch("");
    setManualTab(null);
  };

  return {
    selectedPair,
    search,
    setSearch,
    filteredPairs,
    activeTab,
    setActiveTab: setManualTab,
    handleSelect,
  };
};
